<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="itemsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
            :height="$vuetify.breakpoint.mdAndDown ? '75vh' : '66vh'"
            fixed-header
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">HS CODES</h1>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openCreateItem"
                            class="mx-2"
                            v-if="editSettingsPermission"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                />
            </template>
            <template v-slot:[`header.description`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByDescription"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditItem(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn small icon @click="openDeleteItem(item)">
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PROCESSES FORM-->
        <v-dialog
            v-model="openItemForm"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <HSCodesForm
                v-if="openItemForm"
                :createForm="createForm"
                :item="selectedItem"
                :originalItem="originalItem"
                :HSCodeSetting="setting"
                @addItem="addItem"
                @replaceItem="replaceItem"
                @close="closeEditItem"
            />
        </v-dialog>
        <!--Delete Process-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Process</v-card-title>
                <v-card-text>
                    are you sure you want to delete this HS code?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteItem">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteItem"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'HSCodes',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        HSCodesForm: () => import('@/components/HSCodes/HSCodesForm.vue'),
    },
    data: () => ({
        loading: false,
        items: [],
        selectedItem: {},
        originalItem: {},
        createForm: true,
        openItemForm: false,
        openDeleteWarning: false,
        HSCodeSetting: {},
        headers: [
            {
                text: 'HS CODE',
                value: 'name',
                sortable: false,
                width: '200',
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                sortable: false,
            },
        ],
        searchByName: '',
        searchByDescription: '',
        editSettingsPermission: false,
    }),
    async mounted() {
        try {
            const {
                data: { user },
            } = await API.getMyInfo()
            this.editSettingsPermission = user.permissions.includes(
                'editSettings'
            )
            if (this.editSettingsPermission) {
                this.headers.push({
                    text: 'ACTIONS',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                })
            }
            this.loading = true
            this.items = await API.getHSCodes(this.setting.id)
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        itemsFiltered() {
            const conditions = []
            if (this.searchByName) {
                conditions.push(this.filterByName)
            }
            if (this.searchByDescription) {
                conditions.push(this.filterByDescription)
            }
            if (conditions.length > 0) {
                return this.items.filter(item => {
                    return conditions.every(condition => {
                        return condition(item)
                    })
                })
            }
            return this.items
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByName(item) {
            return (item.name || '')
                .toLowerCase()
                .includes(this.searchByName.toLowerCase())
        },
        filterByDescription(item) {
            return (item.description || '')
                .toLowerCase()
                .includes(this.searchByDescription.toLowerCase())
        },
        replaceItem(itemToUpdate) {
            const index = this.items.findIndex(
                item => item.id == itemToUpdate.id
            )
            if (index >= 0) {
                this.items.splice(index, 1, {
                    ...this.items[index],
                    ...itemToUpdate,
                })
            }
        },
        addItem(item) {
            this.items.push(item)
            this.searchByName = ''
            this.searchByDescription = ''
        },
        removeItem() {
            const index = this.items.findIndex(
                item => item.id == this.selectedItem.id
            )
            if (index >= 0) {
                this.items.splice(index, 1)
            }
        },
        async deleteItem() {
            try {
                this.loading = true
                await API.deleteHSCode({
                    settingId: this.setting.id,
                    id: this.selectedItem.id,
                })
                this.removeItem()
                this.closeDeleteItem()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteItem() {
            this.selectedItem = {}
            this.openDeleteWarning = false
        },
        openDeleteItem(item) {
            this.selectedItem = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditItem() {
            this.createForm = true
            this.selectedItem = {}
            this.originalItem = {}
            this.openItemForm = false
        },
        openEditItem(item) {
            this.createForm = false
            this.selectedItem = _.cloneDeep(item)
            this.originalItem = _.cloneDeep(item)
            this.openItemForm = true
        },
        openCreateItem() {
            this.createForm = true
            this.openItemForm = true
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    position: sticky;
    z-index: 1;
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
.v-data-table-header th {
    background-color: #eeeeee !important;
}
</style>
